/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Cost Analytics Data Composable
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// Replaces the prior /api/mocks/cost/* fetches with real queries against
/// SNOWFLAKE.ACCOUNT_USAGE via useSnowflake(). Each fetcher returns the same
/// shape the cost components were already consuming, so no component changes
/// are required.
import {
  overviewKpiSql,
  overviewSparklineSql,
  trendSql,
  byWarehouseSql,
  byUserSql,
  anomaliesSql,
  heatmapSql,
  recommendationsSql,
  spendByWarehouse6mSql,
  rangeToDays,
  type DateRangeArg,
} from '~/scripts/queries/costQueries'
import {
  topQueriesForWarehouseDaySql,
  topUsersForWarehouseDaySql,
} from '~/scripts/queries/anomalyQueries'

// Re-export so dashboard pages can keep importing the type from one place.
export type { DateRangeArg }

// ////////////////////////////////////////////////////////////////
// Anomaly drill-down result types — exposed alongside the fetchers
// so the AnomalyInvestigationDrawer (and any future surface that
// wants to inspect a single (warehouse, day) pair) can type its
// props without re-declaring the row shape.
// ////////////////////////////////////////////////////////////////

export interface AnomalyHeavyQuery {
  queryId: string
  userName: string
  roleName: string
  warehouseSize: string
  startTime: string
  endTime: string
  execSeconds: number
  bytesScanned: number
  rowsProduced: number
  cloudServicesCost: number
  executionStatus: string
  queryType: string
  queryPreview: string
}

export interface AnomalyTopUser {
  userName: string
  queryCount: number
  execSeconds: number
  /** Percent of total execution time on this warehouse, this day. */
  execSharePct: number
  cloudServicesCost: number
  lastQueryAt: string
}

// ////////////////////////////////////////////////////////////////
// Tiny helper — run a query, throw on failure, return rows.
// ////////////////////////////////////////////////////////////////
async function runQuery(query: string): Promise<any[]> {
  const response: any = await useSnowflake({ query })
  if (response?.status !== 'SUCCESS' || !response.data) {
    const message = response?.error || 'Snowflake query returned no data'
    console.error('[useCostData] Snowflake query failed:', message, '\n', query)
    throw new Error(typeof message === 'string' ? message : JSON.stringify(message))
  }
  return response.data as any[]
}

export const useCostData = () => {
  // ////////////////////////////////////////////////////////////////
  // Overview - KPI cards on the cost dashboard.
  // ////////////////////////////////////////////////////////////////
  /** Fetches the four KPI cards + sparkline. Both sub-queries respect
   *  the dashboard's selected `range` so totals, average, trend, and
   *  the sparkline track whatever the user picked in the filter bar. */
  const fetchOverview = async (range?: DateRangeArg) => {
    const [kpiRows, sparkRows] = await Promise.all([
      runQuery(overviewKpiSql(range)),
      runQuery(overviewSparklineSql(range)),
    ])

    const kpi = kpiRows[0] || {}
    return {
      totalMonthlyCost: Number(kpi.totalMonthlyCost) || 0,
      totalMonthlyCostChange: Number(kpi.totalMonthlyCostChange) || 0,
      averageDailyCost: Number(kpi.averageDailyCost) || 0,
      averageDailyCostTrend: (kpi.averageDailyCostTrend || 'stable') as 'up' | 'down' | 'stable',
      costPerQuery: Number(kpi.costPerQuery) || 0,
      projectedMonthlyCost: Number(kpi.projectedMonthlyCost) || 0,
      sparklineData: sparkRows.map((r: any) => Number(r.cost) || 0),
    }
  }

  // ////////////////////////////////////////////////////////////////
  // Trend - daily cost breakdown for the stacked area chart.
  // ////////////////////////////////////////////////////////////////
  /** Accepts a {start, end} range (preferred) or a legacy string range
   *  ('7d' | '30d' | '90d' | '1y'). Legacy callers without the filter
   *  bar still work; new callers get custom-range support. */
  const fetchTrend = async (rangeOrPreset: DateRangeArg | string = '30d') => {
    const arg: DateRangeArg | number = typeof rangeOrPreset === 'string'
      ? rangeToDays(rangeOrPreset)
      : rangeOrPreset
    const rows = await runQuery(trendSql(arg))
    return {
      data: rows.map((r: any) => ({
        date: String(r.date),
        computeCost: Number(r.computeCost) || 0,
        storageCost: Number(r.storageCost) || 0,
        dataTransferCost: Number(r.dataTransferCost) || 0,
        cloudServicesCost: Number(r.cloudServicesCost) || 0,
      })),
    }
  }

  // ////////////////////////////////////////////////////////////////
  // Cost by warehouse + percent-of-total + query count.
  // Defaults to month-to-date when no range is supplied (preserves
  // the prior behavior for any caller that hasn't been migrated).
  // ////////////////////////////////////////////////////////////////
  const fetchByWarehouse = async (range?: DateRangeArg) => {
    const rows = await runQuery(byWarehouseSql(range))
    return {
      warehouses: rows.map((r: any) => ({
        name: String(r.name),
        size: String(r.size),
        cost: Number(r.cost) || 0,
        percentOfTotal: Number(r.percentOfTotal) || 0,
        queryCount: Number(r.queryCount) || 0,
      })),
    }
  }

  // ////////////////////////////////////////////////////////////////
  // Cost by user - prorated warehouse credits + cloud-services credits.
  // `lastActive` comes back ISO; convert to a human-readable relative
  // string so the existing column doesn't need its formatter changed.
  // Defaults to month-to-date when no range is supplied.
  // ////////////////////////////////////////////////////////////////
  const fetchByUser = async (range?: DateRangeArg) => {
    const rows = await runQuery(byUserSql(range))
    return {
      users: rows.map((r: any, index: number) => ({
        id: Number(r.id) || index + 1,
        name: String(r.name),
        email: r.email ? String(r.email) : '',
        totalQueries: Number(r.totalQueries) || 0,
        totalCost: Number(r.totalCost) || 0,
        avgCostPerQuery: Number(r.avgCostPerQuery) || 0,
        lastActive: relativeTimeFromIso(r.lastActive),
      })),
    }
  }

  // ////////////////////////////////////////////////////////////////
  // Statistical anomalies (>2σ daily warehouse spend) — over the
  // chosen range, with the rolling baseline pulled from 14 days
  // BEFORE range.start so the first visible day is comparable.
  // ////////////////////////////////////////////////////////////////
  const fetchAnomalies = async (range?: DateRangeArg) => {
    const rows = await runQuery(anomaliesSql(range))
    return {
      anomalies: rows.map((r: any) => ({
        id: String(r.id),
        timestamp: String(r.timestamp),
        type: (r.type === 'unusual_pattern' ? 'unusual_pattern' : 'spike') as
          | 'spike'
          | 'unusual_pattern',
        severity: (r.severity || 'info') as 'info' | 'warning' | 'critical',
        warehouse: String(r.warehouse),
        user: r.user ? String(r.user) : '',
        cost: Number(r.cost) || 0,
        expectedCost: Number(r.expectedCost) || 0,
        description: String(r.description || ''),
      })),
    }
  }

  // ////////////////////////////////////////////////////////////////
  // Rule-based recommendations (idle / oversized / long-running).
  // All three rules use the dashboard's selected `range`.
  // ////////////////////////////////////////////////////////////////
  const fetchRecommendations = async (range?: DateRangeArg) => {
    const rows = await runQuery(recommendationsSql(range))
    return {
      recommendations: rows.map((r: any) => ({
        id: String(r.id),
        type: r.type as 'idle_warehouse' | 'oversized' | 'long_query' | 'duplicate',
        title: String(r.title),
        description: String(r.description),
        potentialSavings: Number(r.potentialSavings) || 0,
        action: String(r.action),
        priority: (r.priority || 'low') as 'high' | 'medium' | 'low',
      })),
    }
  }

  // ////////////////////////////////////////////////////////////////
  // 7-day × 24-hour cost heatmap over the chosen range.
  // ////////////////////////////////////////////////////////////////
  const fetchHeatmap = async (range?: DateRangeArg) => {
    const rows = await runQuery(heatmapSql(range))
    return {
      data: rows.map((r: any) => ({
        dayOfWeek: Number(r.dayOfWeek) || 0,
        hour: Number(r.hour) || 0,
        cost: Number(r.cost) || 0,
      })),
    }
  }

  // ////////////////////////////////////////////////////////////////
  // Warehouse Spend (Last 6 Months) — pivot the long-format SQL
  // result into the stacked-bar shape the SpendChart expects:
  //   { months: ['Apr 2026', 'May 2026', ...],
  //     datasets: [{ label, color, data: number[] }, ...] }
  // ////////////////////////////////////////////////////////////////
  const fetchSpendByWarehouse = async () => {
    const rows = await runQuery(spendByWarehouse6mSql) as Array<{
      month: string
      warehouseName: string
      cost: number
    }>

    // Collect distinct months + warehouses in stable order.
    // Months: ascending by canonical YYYY-MM. Warehouses: insertion order
    // from the SQL, which is already month-asc + cost-desc, so heavier
    // warehouses tend to land first in the legend.
    const monthsSet = new Set<string>()
    const warehouses: string[] = []
    const seenWarehouse = new Set<string>()
    for (const r of rows) {
      monthsSet.add(String(r.month))
      const wh = String(r.warehouseName)
      if (!seenWarehouse.has(wh)) {
        seenWarehouse.add(wh)
        warehouses.push(wh)
      }
    }
    const months = Array.from(monthsSet).sort()

    // Build a (warehouse → month → cost) lookup so we can fill missing
    // (warehouse, month) cells with 0 in the chart's `data` array. Without
    // this, a warehouse that didn't run at all in May would have a
    // shorter `data` array than May had bars and Chart.js would
    // misalign the stacks.
    const lookup: Record<string, Record<string, number>> = {}
    for (const r of rows) {
      const wh = String(r.warehouseName)
      lookup[wh] ??= {}
      lookup[wh][String(r.month)] = Number(r.cost) || 0
    }

    const datasets = warehouses.map((wh) => ({
      label: wh,
      color: pickWarehouseColor(wh),
      data: months.map((m) => lookup[wh]?.[m] ?? 0),
    }))

    return {
      months: months.map(formatYearMonth),
      datasets,
    }
  }

  // ////////////////////////////////////////////////////////////////
  // Anomaly drill-down — load the (warehouse, day)-scoped slices the
  // AnomalyInvestigationDrawer needs to explain a spike. Fired in
  // parallel from the drawer; either side can fail without blanking
  // the other (the drawer renders empty-state per section).
  // ////////////////////////////////////////////////////////////////
  const fetchAnomalyTopQueries = async (
    warehouse: string,
    day: string,
  ): Promise<AnomalyHeavyQuery[]> => {
    const rows = await runQuery(topQueriesForWarehouseDaySql(warehouse, day))
    return rows.map((r: any) => ({
      queryId: String(r.queryId || ''),
      userName: String(r.userName || ''),
      roleName: String(r.roleName || ''),
      warehouseSize: String(r.warehouseSize || ''),
      startTime: String(r.startTime || ''),
      endTime: String(r.endTime || ''),
      execSeconds: Number(r.execSeconds) || 0,
      bytesScanned: Number(r.bytesScanned) || 0,
      rowsProduced: Number(r.rowsProduced) || 0,
      cloudServicesCost: Number(r.cloudServicesCost) || 0,
      executionStatus: String(r.executionStatus || ''),
      queryType: String(r.queryType || ''),
      queryPreview: String(r.queryPreview || ''),
    }))
  }

  const fetchAnomalyTopUsers = async (
    warehouse: string,
    day: string,
  ): Promise<AnomalyTopUser[]> => {
    const rows = await runQuery(topUsersForWarehouseDaySql(warehouse, day))
    return rows.map((r: any) => ({
      userName: String(r.userName || ''),
      queryCount: Number(r.queryCount) || 0,
      execSeconds: Number(r.execSeconds) || 0,
      execSharePct: Number(r.execSharePct) || 0,
      cloudServicesCost: Number(r.cloudServicesCost) || 0,
      lastQueryAt: String(r.lastQueryAt || ''),
    }))
  }

  return {
    fetchOverview,
    fetchTrend,
    fetchByWarehouse,
    fetchByUser,
    fetchAnomalies,
    fetchRecommendations,
    fetchHeatmap,
    fetchSpendByWarehouse,
    fetchAnomalyTopQueries,
    fetchAnomalyTopUsers,
  }
}

// //////////////////////////////////////////////////////////////////
// Format an ISO timestamp into "X minutes/hours/days ago" — keeps
// the existing CostByUserTable column rendering identical to mocks.
// //////////////////////////////////////////////////////////////////
function relativeTimeFromIso(iso: unknown): string {
  if (!iso) return 'unknown'
  const ts = typeof iso === 'string' ? Date.parse(iso) : NaN
  if (isNaN(ts)) return 'unknown'
  const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000))
  if (diffSec < 60) return `${diffSec} seconds ago`
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`
  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`
}

// //////////////////////////////////////////////////////////////////
// Warehouse color palette for the SpendChart.
// //////////////////////////////////////////////////////////////////
/// Tailwind-aligned, with enough variety to keep adjacent stacks
/// visually distinct. Order matters — adjacent palette entries should
/// contrast well, since two warehouses with similar names will hash to
/// nearby indices.
const WAREHOUSE_COLOR_PALETTE = [
  'rgba(99, 102, 241, 0.85)',   // indigo-500
  'rgba(34, 197, 94, 0.85)',    // green-500
  'rgba(168, 85, 247, 0.85)',   // purple-500
  'rgba(20, 184, 166, 0.85)',   // teal-500
  'rgba(244, 114, 182, 0.85)',  // pink-400
  'rgba(59, 130, 246, 0.85)',   // blue-500
  'rgba(245, 158, 11, 0.85)',   // amber-500
  'rgba(139, 92, 246, 0.85)',   // violet-500
  'rgba(6, 182, 212, 0.85)',    // cyan-500
  'rgba(236, 72, 153, 0.85)',   // pink-500
  'rgba(217, 70, 239, 0.85)',   // fuchsia-500
  'rgba(132, 204, 22, 0.85)',   // lime-500
]

/**
 * Pick a deterministic palette color for a warehouse name. FNV-1a 32-bit
 * hash mod palette length — same name always lands on the same color across
 * reloads, sessions, and (importantly) across the multiple cost surfaces
 * that show this chart so a warehouse keeps its identity visually.
 */
function pickWarehouseColor(name: string): string {
  let h = 2166136261
  for (let i = 0; i < name.length; i++) {
    h ^= name.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return WAREHOUSE_COLOR_PALETTE[Math.abs(h) % WAREHOUSE_COLOR_PALETTE.length]
}

/**
 * Convert a canonical 'YYYY-MM' string into a display-friendly 'MMM YYYY'
 * (e.g. 'Apr 2026'). Falls back to the raw value if the input doesn't
 * match the expected shape — better to show something than nothing.
 */
function formatYearMonth(ym: string): string {
  const match = ym.match(/^(\d{4})-(\d{2})$/)
  if (!match) return ym
  const monthIdx = parseInt(match[2], 10) - 1
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  if (monthIdx < 0 || monthIdx >= 12) return ym
  return `${monthNames[monthIdx]} ${match[1]}`
}
